import { SpaCareReturn } from '../components/SpaCareReturn';
import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { Disclosure } from '../components/Disclosure';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Hot Tub Cover Lifts in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'We keep Cover-Valet, Cover Rockit and Cover Ex cover lifts in stock at Pegasus Spas in Brooklyn, Michigan, and can order other lifts and brackets to fit your spa.';
const CANONICAL = 'https://www.pegasus-spas.com/coverlifters.html';

/** The cover lifts we normally keep in stock. Edit this list as the lineup changes. */
const stockedLifts = [{
  name: 'Cover-Valet',
  summary: 'Gas-spring assisted, so the cover rises with less effort. Mounts to the cabinet or deck.',
  image: 'https://covervalet.com/wp-content/uploads/2022/03/cover-valet-gif.gif',
  alt: 'Cover-Valet lift folding a spa cover back with gas-spring assist',
  source: 'https://covervalet.com/cover-lifters/cover-valet/'
}, {
  name: 'Cover Rockit',
  summary: 'Slides under the spa instead of mounting to it, so there is no drilling into the cabinet.',
  image: 'https://covervalet.com/wp-content/uploads/2021/11/Untitled-design-54.gif',
  alt: 'Cover Rockit undermount lift rolling a spa cover back',
  source: 'https://covervalet.com/cover-lifters/cover-rock-it/'
}, {
  name: 'Cover Ex',
  summary: 'A simple cabinet-mounted lift without spring assist. Straightforward and dependable.',
  image: 'https://covervalet.com/wp-content/uploads/2024/04/EX-GIF-500x336-1.gif',
  alt: 'Cover Ex cabinet-mounted lift moving a spa cover off the tub',
  source: 'https://covervalet.com/cover-lifters/cover-ex/'
}];
const fitNotes = ['If you know them, note the spa brand, model and approximate year. If not, send a few photos and we’ll help figure out what matters.', 'Note the space behind and beside the spa, plus any deck, railing or enclosure.', 'Photos or rough measurements are plenty to get started.'];
const relatedLinks = [{
  label: 'Replacement Covers',
  href: routes.covers
}, {
  label: 'Hot Tub Steps',
  href: routes.spaSteps
}, {
  label: 'Spa Care',
  href: routes.spaCare
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function CoverLiftsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1118" data-mp="jfjwvc7mwfo1">
      <SpaCareReturn data-id="element-1119" data-mp="jgblg32tdd4t" />
      {/* Intro band */}
      <section aria-labelledby="lifts-h1" className="w-full bg-water" data-id="element-1120" data-mp="jcxhiqei4vv9">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1121" data-mp="5hbtomk08i4f">
          <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1122" data-mp="9hvq1nwr7hpi">Hot Tub Cover Lifts</p>
          <h1 id="lifts-h1" className="mt-2 max-w-3xl text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1123" data-mp="x7q58c4jpicx">
            Make Your Spa Cover Easier to Handle
          </h1>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1124" data-mp="oe4papgn9v1x">
            A cover lift is one of those additions that changes how often the spa actually gets
            used. It takes the work out of moving the cover and keeps it supported and off the
            ground. Which one fits depends on your spa, your cover and the space around the tub,
            and we&rsquo;re happy to help you sort that out.
          </p>
        </div>
      </section>

      {/* Stocked lifts */}
      <section aria-labelledby="stocked-heading" className="w-full bg-white" data-id="element-1125" data-mp="3ocxr1wvskje7">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1126" data-mp="3jfxklkv15emk">
          <h2 id="stocked-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1127" data-mp="3fecdyh939wkt">
            The Lifts We Keep in Stock
          </h2>
          <p className="mt-3 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1128" data-mp="b0duuvtkiec9">
            These three are the lifts we carry every day at the Brooklyn showroom. Stop in to see
            how each one moves, or call and we&rsquo;ll talk through which suits your spa.
          </p>

          <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3" data-id="element-1129" data-mp="ypgn0xltvpl0">
            {stockedLifts.map(lift => <li key={lift.name} className="flex min-w-0 flex-col" data-id="element-1130" data-mp="txf26nalgqjd">
                <div className="aspect-[3/2] w-full overflow-hidden rounded-card border border-ink/10 bg-white" data-id="element-1131" data-mp="2clhxd0d4z51r">
                  <img src={lift.image} alt={lift.alt} className="h-full w-full object-contain" loading="lazy" decoding="async" data-id="element-1132" data-mp="2hj9rm1197394" />
                </div>
                <h3 className="mt-5 font-display text-[26px] font-semibold leading-8 text-ink" data-id="element-1133" data-mp="2vjub6zezt33o">
                  {lift.name}
                </h3>
                <p className="mt-2 text-[16.5px] leading-6 text-ink/80" data-id="element-1134" data-mp="30hm8ya2bjjfs">{lift.summary}</p>
                <p className="mt-auto pt-3 text-[15px] leading-6 text-pine" data-id="element-1135" data-mp="35aowa0gi5wnj">
                  Normally in stock in Brooklyn
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Other lifts */}
      <section aria-labelledby="other-heading" className="w-full border-y border-ink/10 bg-water" data-id="element-1136" data-mp="3e8hd591vhcd1">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1137" data-mp="3j72bt41h7nzd">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1138" data-mp="1j7f7tbls9swj">
            <div className="lg:col-span-5" data-id="element-1139" data-mp="1e9doqqocxtl2">
              <h2 id="other-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1140" data-mp="1swys8lp59uti">
                Need Something Different?
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1141" data-mp="21xdh1v1bhs1r">
                Some spas and spaces need a different lift or a special bracket. We can order other
                lifts and brackets, and we&rsquo;ll help you find the one that fits.
              </p>
            </div>

            <div className="lg:col-span-7" data-id="element-1142" data-mp="26wztw3d8h6a1">
              <p className="text-[15px] font-bold uppercase text-ink/60" data-id="element-1143" data-mp="26y1eayg5354f">Helpful to share</p>
              <ol className="mt-3 border-t border-ink/15" data-id="element-1144" data-mp="hil7vqtlnxi8">
                {fitNotes.map((note, index) => <li key={note} className="flex gap-4 border-b border-ink/15 py-4" data-id="element-1145" data-mp="cka0dmqs1z36" data-mpi={index}>
                    <span className="shrink-0 text-[15px] font-bold text-pegasus-magenta" data-id="element-1146" data-mp="2o7dh2szoijsc">
                      {index + 1}
                    </span>
                    <p className="text-[16.5px] leading-6 text-ink/80" data-id="element-1147" data-mp="2eg91br26iuwg">{note}</p>
                  </li>)}
              </ol>

              <div className="mt-6" data-id="element-1148" data-mp="2jeamchj614ak">
                <Disclosure label="Manufacturer details" data-id="element-1149" data-mp="2jd97s32xxzo3">
                  <p className="max-w-2xl text-[16px] leading-6 text-ink/75" data-id="element-1150" data-mp="25ejaong4fzzq">
                    Every lift has its own clearance, mounting and cover-size requirements, and they
                    change from one manufacturer to the next. Before you order, we check those
                    details against your spa so the lift you bring home is the right one.
                  </p>
                  <ul className="mt-3 flex flex-col gap-x-6 sm:flex-row sm:flex-wrap" data-id="element-1151" data-mp="3pv65k86xpqoe">
                    {stockedLifts.map(lift => <li key={lift.name} data-id="element-1152" data-mp="3utpsobx9l8ic">
                        <a href={lift.source} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta" data-id="element-1153" data-mp="2ikw1ob7jy2dt">
                          {lift.name} details
                          <span className="sr-only" data-id="element-1154" data-mp="1yutt3u5lfxt4"> (opens in a new tab)</span>
                        </a>
                      </li>)}
                  </ul>
                </Disclosure>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section aria-labelledby="pair-heading" className="w-full bg-ink" data-id="element-1155" data-mp="3kahjy54bnz0g">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1156" data-mp="3k8ey7xa7cs1j">
          <div className="max-w-3xl" data-id="element-1157" data-mp="3fbxzya9agxaq">
            <h2 id="pair-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1158" data-mp="3muxi6cl0kisi">
              Pair a New Cover With Easier Everyday Use
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1159" data-mp="et9mrdmzmop">
              If your cover is due for replacement anyway, it&rsquo;s a good moment to sort out the
              lift at the same time.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1160" data-mp="33xmjou77hte4">
            <SiteLink to={routes.covers} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1161" data-mp="38uczrhj0gozj">
              Build a Replacement Cover
            </SiteLink>
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1162" data-mp="t3id2x26043m">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1163" data-mp="14av2k9szpxr9" />
              Call or text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1164" data-mp="193ep3w9no387">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1165" data-mp="ldbxfh9nkza4" />
              Visit Our Brooklyn Showroom
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-1166" data-mp="v9596yfjsoqu">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1167" data-mp="21r2856va2lsq">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1168" data-mp="26pc43jottqfe">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1169" data-mp="3raa5l0ynwlqn">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1170" data-mp="3m8t8zhn7nlfv">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1171" data-mp="33j4qio0jpxhe" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}